import useGetMessages from '@/hooks/queries/chat-api/useGetMessages';
import useGetOrganization from '@/hooks/queries/organization-api/useGetOrganization';
import useSocket, { SendMessagePayload } from '@/hooks/useSocket';
import { useRootStore } from '@/stores/root-store';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  Button,
  ChatList,
  IChatItemProps,
  Input,
  MessageList
} from 'react-chat-elements';
import 'react-chat-elements/dist/main.css';

const AssistantChat: React.FC = () => {
  const { data: organization } = useGetOrganization();
  const isEnabledSocket = organization?.additionalData
    ? !!JSON.parse(organization?.additionalData).messages
    : false;
  const { sendMessage, markChatAsRead } = useSocket({
    enabled: isEnabledSocket
  });
  const chats = useRootStore((state) => state.chats);
  const selectedChatId = useRootStore((state) => state.selectedChatId);
  const setSelectedChatId = useRootStore((state) => state.setSelectedChatId);
  const setMessagesOnOpenChat = useRootStore(
    (state) => state.setMessagesOnOpenChat
  );
  const [inputValue, setInputValue] = useState('');
  const messageListReferance = useRef<null | HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { data, isLoading } = useGetMessages(selectedChatId);

  const selectedChat = chats.find((c) => c.id === selectedChatId);

  const handleSendMessage = () => {
    if (inputValue.trim() === '' || !selectedChatId) return;

    if (selectedChat) {
      const payload: SendMessagePayload = {
        channel: 'whatsapp',
        contactId: selectedChat.id,
        botId: selectedChat.botId,
        message: inputValue,
        messageType: 'text'
      };
      sendMessage(payload);
      setInputValue('');
    }
  };

  useEffect(() => {
    if (data && selectedChatId) {
      setMessagesOnOpenChat(data, selectedChatId);
      markChatAsRead({
        channel: 'whatsapp',
        contactId: selectedChatId
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedChatId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [selectedChat]);

  return (
    // Height div: total - navbar - padding - margin
    <div className="flex h-[calc(100dvh-60px-48px-16px)]">
      <div className="w-4/12 border-r border-[#ccc] overflow-y-auto">
        <ChatList
          id={1}
          className="chat-list"
          lazyLoadingImage=""
          dataSource={chats.sort((a, b) =>
            new Date(b.date).getTime() < new Date(a.date).getTime() ? -1 : 1
          )}
          onClick={(chat: IChatItemProps) =>
            setSelectedChatId(chat.id as string)
          }
        />
      </div>

      <div className="flex flex-col flex-1">
        {selectedChat && !isLoading && (
          <>
            <div className="flex-1 overflow-y-auto">
              <MessageList
                referance={messageListReferance}
                className="message-list"
                lockable={false}
                toBottomHeight={'100%'}
                dataSource={
                  selectedChat.messages.sort((a, b) =>
                    new Date(a.date).getTime() < new Date(b.date).getTime()
                      ? -1
                      : 1
                  ) ?? []
                }
                onDownload={(msg) => {
                  if (msg.type === 'file') {
                    const link = document.createElement('a');
                    link.href = msg.data.uri ?? '';
                    link.download = msg.data.name ?? 'download';
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                }}
                // onReplyMessageClick={(msg: any) => {
                //   console.log('Reply to message:', msg);
                //   // Scroll on to message by id
                //   const messageElement = document.getElementById(msg.id);
                //   if (messageElement) {
                //     messageElement.scrollIntoView({
                //       behavior: 'smooth',
                //       block: 'center'
                //     });
                //   }
                // }}
              />
              <div ref={messagesEndRef} />
            </div>

            <div className="p-3">
              <Input
                placeholder="Escribí un mensaje..."
                multiline={false}
                value={inputValue}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setInputValue(e.target.value)
                }
                maxHeight={100}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                rightButtons={
                  <Button
                    text=">"
                    className="flex flex-row items-center justify-center w-10 h-10 text-2xl font-bold rounded-full font-[Arial]"
                    onClick={handleSendMessage}
                  />
                }
              />
            </div>
          </>
        )}
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <p>Cargando mensajes...</p>
          </div>
        )}
        {!selectedChat && (
          <div className="flex items-center justify-center h-full">
            <p>Seleccioná un chat para ver los mensajes.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantChat;
